import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CoreCOMService } from '../../services/com.service';
import { IOSHelperService } from '../../services/IOSHelper.service';
import { CoreLanguageService } from '../../services/language.service';

/**ControlValueAccessor: Defines an interface that acts as a bridge between the Angular forms API and a native element in the DOM. 
 * by implementing this interface we can use a component as a form control and use model binding and validation.
 * NG_VALUE_ACCESSOR must be add to provider.
 * this interface has 4 method that should be implement in component:
 * writeValue, registerOnTouched, registerOnTouched, setDisabledState 
*/

@Component({
  selector: 'ya-customInput',
  templateUrl: './customInput.component.html',
  styleUrls: ['./customInput.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR, useExisting: CustomInputComponent, multi: true
  }]
})
export class CustomInputComponent implements OnInit, ControlValueAccessor {

  public registerPasswordVisibility: boolean = false; // used for show and hide password

  @Input() inputType: string;
  @Input() inputMaxLength: number = 200;
  @Input() inputPlaceholder: string | (() => string);
  @Input() inputTrim: boolean = false;

  // true if ismobile; tohandle enter keyup in desktop result value should be false
  @Output() enterKeyUp = new EventEmitter<boolean>();
  @ViewChild("CustomInput", { read: ElementRef }) inputElementRef: ElementRef;

  /**On blur (or equivalent), your class should call the registered function to allow the forms API to update itself.(used by ControlValueAccessor) */
  onTouched = () => { };

  /**When the value changes in the UI, call the registered function to allow the forms API to update itself.(used by ControlValueAccessor) */
  onChange = (newValue: any) => { };

  constructor(private _iosHelperService: IOSHelperService
    , private _renderer: Renderer2
    , private _coreComService: CoreCOMService
    , private _coreLanguageService: CoreLanguageService) { }

  /** Writes a new value to the element.(used by ControlValueAccessor)
   * This method is called by the forms API to write to the view when programmatic changes from model to view are requested.
   */
  writeValue(value: string) {
    const me = this;
    if (value) {
      me._renderer.setProperty(this.inputElementRef.nativeElement, 'value', value);
      this.onChange(value);
    } else {
      this.onChange(null);
    }
  }

  /**Registers a callback function that is called by the forms API on initialization to update the form model on blur.(used by ControlValueAccessor) */
  registerOnTouched(fn: any) {
    const me = this;
    me.onTouched = fn;
  }

  /** Registers a callback function that is called when the control's value changes in the UI.(used by ControlValueAccessor)
  * This method is called by the forms API on initialization to update the form model when values propagate from the view to the model.
  * When the value changes in the UI, call the registered function to allow the forms API to update itself   
  */
  registerOnChange(fn: any) {
    const me = this;
    me.onChange = fn;
  }

  /**Function that is called by the forms API when the control status changes to or from 'DISABLED'.(used by ControlValueAccessor) */
  setDisabledState(isDisabled: boolean): void {
    this._renderer.setProperty(this.inputElementRef.nativeElement, 'disabled', isDisabled);
  }

  ngOnInit() {
  }

  /** Trim white spaces from input value */
  private trimInputValue(value: string): string {
    return value.replace(/\s+/g, '');
  }

  /** Input change event */
  public onInputChange() {
    const me = this;
    const inputValue = me.inputElementRef.nativeElement.value;
    const value = me.inputTrim ? me.trimInputValue(inputValue) : inputValue;
    me.onChange(value);
  }

  /** Handle onBlur event */
  public onBlur() {
    const me = this;
    const currentValue = me.inputElementRef.nativeElement.value;
    if (me.inputTrim && currentValue !== me.trimInputValue(currentValue)) {
      me.inputElementRef.nativeElement.value = me.trimInputValue(currentValue);
    }
    me.onTouched();
    me.setWebView();
  }

  public blur() {
    const me = this;
    me.inputElementRef.nativeElement.blur();
  }

  /**set placeholder for input */
  public setPlaceholder() {
    const me = this;
    if (typeof me.inputPlaceholder === "string") {
      return me._coreLanguageService.value(me.inputPlaceholder);
    } else if (typeof me.inputPlaceholder === "function") {
      return me.inputPlaceholder();
    } else {
      return "";
    }
  }

  public handleKeyup(event) {
    const me = this;
    if (event.keyCode === 13) {
      if (me._coreComService.environment.client.isMobile) {
        (me.inputElementRef.nativeElement as HTMLElement).blur();
        me.enterKeyUp.emit(true);
      } else {
        me.enterKeyUp.emit(false);
      }
    }
  }

  public handleKeyDown(event) {
    if (event.currentTarget.value.length === 0 && event.which === 32) {
      event.preventDefault();
    }
  }

  /**solve problem for IOS keyboard. set the page size after closing the keyboard */
  private setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

}

